import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GroupsIcon from "@mui/icons-material/Groups";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";

import { useCheckInPhase } from "/imports/hooks/useCheckInOpen";
import AddToCalendarMenu from "/imports/ui/components/shared/AddToCalendarMenu";
import { formatDuration } from "../dashboardHelpers";
import { BOOKING_PILL_COLOR } from "../dashboardConstants";
import CheckInHint from "./CheckInHint";
import RowShell from "./RowShell";

// Buttons lead the action line at every width, so what's worth tapping never
// sits behind the status chips.
const ACTION_ORDER = { order: -1 };

// Server reasons a package-paid booking can't be moved because of the pack itself.
const PACK_HINTS = {
  PACK_FROZEN: "Package frozen · contact club",
  PACK_EXPIRED: "Package expired · contact club",
  PACK_GONE: "Package inactive · contact club"
};

// Cancel and Reschedule share the pill. The theme rounds chips to 6px, so a
// button that swaps places with the "Contact club to cancel" chip takes the same corner.
const PILL_BUTTON_SX = {
  ...ACTION_ORDER,
  height: "1.75rem",
  minWidth: "auto",
  px: 1.25,
  borderRadius: "0.375rem",
  fontSize: "0.75rem",
  fontWeight: 500,
  textTransform: "none",
  "& .MuiButton-startIcon": { ml: 0, mr: 0.5 }
};

function BookingRow({ booking, onCancel, onReschedule, onPayNow, onSettleBooking, onCheckIn, payLoadingId, onViewInvoice, onViewGame, onViewClass }) {
  // Server isPast is computed in the club's zone (startDate is fake UTC, so a
  // local Date compare misreads it by the club offset); fall back only when absent.
  const isFuture = booking.isPast != null ? !booking.isPast : booking.startDate && new Date(booking.startDate) > new Date();
  const isUnpaid = booking.invoiceStatus === "UNPAID";
  // SETTLED = the club rolled this booking's invoice into a consolidated one that
  // was paid in full, so it is paid and cancellable one booking at a time.
  const isPaid = booking.invoiceStatus === "PAID" || booking.invoiceStatus === "SETTLED";
  // Cancelled with the money or package session given back: the invoice still reads PAID,
  // but "Paid" beside CANCELED hides that the client got it back.
  const isRefunded = booking.bookingStatus === "CANCELED" && booking.refunded === true;
  // Cancelled before anything was collected (an unpaid booking released, a session of a
  // pack not yet paid for): "Refunded" would promise money the client never paid.
  const isWrittenOff = booking.bookingStatus === "CANCELED" && booking.writtenOff === true;
  // The refund is raised but the money has not reached the client yet (the card refund
  // failed or is waiting on the club) — "Refunded" would be a promise, not a fact.
  const isRefundPending = booking.bookingStatus === "CANCELED" && booking.refundPending === true;
  // PENDING is a seat held by an unpaid package purchase — it books itself
  // when that payment lands (or releases if it never does), so the row offers
  // no pay/cancel of its own. The server rejects both anyway.
  const isPendingHold = booking.bookingStatus === "PENDING";
  const isNew = booking.bookingStatus === "NEW";
  // Split booking (open game): every participant gets a way back to the game
  // page; a host who never finished the card hold gets nudged to complete it.
  const hasGame = !!booking.game;
  const needsHold = hasGame && booking.game.isHost && booking.game.status === "PENDING_AUTH";
  const seatsLabel = hasGame && booking.game.seatsTotal ? `${booking.game.seatsTaken ?? 0}/${booking.game.seatsTotal}` : null;
  // A class row opens the session page — the same one the timetable leads to,
  // with the roster of who's playing. A cancelled seat is no longer the client's session.
  const canViewClass = booking.type === "category-service-class" && booking.bookingStatus !== "CANCELED" && !!onViewClass;
  // Any future NEW/PENDING booking that is either paid (whatever the checkout
  // method — the server picks the refund destination) or never checked out at
  // all (no invoice; server blocks it inside a charge window) can be cancelled
  // by the client. Bookings with an open UNPAID invoice are the club's to
  // settle or annul, so they only get "Pay Now".
  // A session of a package the club has not collected on yet owes nothing itself — the
  // balance sits on the pack — so the client can drop it like any paid one, and the
  // credit goes back to the pack they will still pay in full.
  const isPendingPackage = booking.invoiceStatus === "PENDING_PACKAGE";
  // Services the club keeps off self-service cancellation (a semi-private lesson
  // needs both players, so one leaving is the club's call). The row points at the
  // club instead of dropping the action silently; the server blocks it too.
  const isClubOnlyCancel = booking.clientCancelDisabled === true;
  const isCancellable = isNew && isFuture && (isPaid || isPendingPackage || !booking.invoiceId);
  const canCancel = isCancellable && !isClubOnlyCancel;
  const showClubOnlyCancelHint = isCancellable && isClubOnlyCancel;
  // The server decides: a PAID class booking, outside the window where cancelling would
  // charge or forfeit it. Unpaid or never-checked-out bookings are cancelled and rebooked.
  const canReschedule = booking.canReschedule === true && isFuture && !!onReschedule;
  // The pack that paid for the session can't pay for another (frozen, expired, gone):
  // the row says so in the button's place rather than dropping the action silently.
  const packHint = PACK_HINTS[booking.rescheduleReason];
  const showPackHint = !!packHint && isNew && isFuture && !!onReschedule;

  // The club can book a client in from the scheduler without checking out, which
  // leaves the booking with no invoice at all — so an unpaid balance shows up as
  // either an open UNPAID invoice or, here, the price agreed at booking time.
  // A booking with no invoice is settled through the Pay Now dialog, where the
  // client picks a package/voucher or pays online — so package-covered bookings
  // still show Pay Now (the client redeems the package there themselves).
  // A live split booking has no invoice BY DESIGN: the host's card hold already
  // covers the whole court and the capture settles it automatically at booking
  // start. Offering Pay Now here would collect the money a second time and the
  // capture would still fire. A friend's claimed-but-unpaid share is different —
  // that has a real UNPAID invoice, so it keeps its Pay Now.
  const splitHoldLive = hasGame && ["PENDING_AUTH", "OPEN", "CAPTURING"].includes(booking.game.status);
  const needsSettle = isNew && !booking.invoiceId && parseFloat(booking.price) > 0 && !splitHoldLive;
  const canPay = isUnpaid || needsSettle;

  // Only the host sits on a hold with nothing to pay. A friend either paid their
  // share (isPaid) or still owes it (canPay), so the chip would contradict the
  // button sitting next to it.
  const showHoldChip = splitHoldLive && booking.game.isHost && !isPaid && !canPay;

  const durationLabel = formatDuration(booking.duration);

  // The server decides eligibility (club setting, booking kind, seat status);
  // the hook only answers "is the window open right now", and re-renders the
  // row the moment it opens so a page left sitting doesn't miss it. An unpaid
  // balance is deliberately not part of this: someone who turned up owing money
  // still turned up, and Pay Now stays alongside.
  const checkedIn = booking.checkIn;
  const checkInPhase = useCheckInPhase(booking.checkInWindow);
  const canCheckIn = checkInPhase === "open";

  // The server only signs a calendar link for an upcoming seat the client holds;
  // the client-side gate keeps a stale row from offering one after it starts.
  const showCalendar = !!booking.calendar && isFuture && booking.bookingStatus !== "CANCELED";

  // The status chip alone still leaves a phone room for the text column. Anything
  // past it — the Paid / Refunded / Not charged / Checked in indicators or a button — does not, and the
  // date line ends up wrapping a word at a time.
  const hasRowExtras =
    isPaid ||
    isRefunded ||
    isRefundPending ||
    isWrittenOff ||
    isPendingHold ||
    showHoldChip ||
    needsHold ||
    Boolean(checkedIn) ||
    canCheckIn ||
    checkInPhase === "before" ||
    showCalendar ||
    canPay ||
    canCancel ||
    canReschedule ||
    showPackHint ||
    showClubOnlyCancelHint;

  return (
    <RowShell
      stack={hasRowExtras}
      onClick={canViewClass ? () => onViewClass(booking) : undefined}
      // Rows rely on hover alone to signal they open; a phone has no hover, so
      // the arrow is what says there's more to see.
      trailing={canViewClass ? <ChevronRightIcon sx={{ fontSize: "1.25rem", color: "text.disabled", flexShrink: 0, ml: -0.5 }} /> : null}
    >
      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="baseline" gap={1}>
          <Typography variant="subtitle2" noWrap>
            {booking.serviceName}
          </Typography>
          {booking.invoiceId && booking.invoiceNumber && (
            <Typography
              variant="caption"
              onClick={(e) => {
                e.stopPropagation();
                onViewInvoice?.({ _id: booking.invoiceId });
              }}
              sx={{
                flexShrink: 0,
                color: "text.secondary",
                fontWeight: 500,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline", color: "text.primary" }
              }}
            >
              #{booking.invoiceNumber}
            </Typography>
          )}
        </Box>
        <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 600 }}>
          {booking.date} at {booking.time}
          {durationLabel && ` • ${durationLabel}`}
          {booking.location && ` — ${booking.location}`}
        </Typography>
        {(booking.trainerName || booking.facilityName) && (
          <Typography variant="body2" color="textSecondary">
            {[booking.trainerName, booking.facilityName].filter(Boolean).join(" • ")}
          </Typography>
        )}
        {hasGame && (
          <Box
            onClick={() => onViewGame?.(booking)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.25,
              py: 0.25,
              cursor: "pointer",
              color: "primary.main",
              "&:hover": { textDecoration: "underline" }
            }}
          >
            <GroupsIcon sx={{ fontSize: "0.9375rem" }} />
            {/* The hold CTA moved to a button in the actions column, so this
                stays a plain way into the game page and never says it twice. */}
            <Typography variant="caption" fontWeight={700}>
              {seatsLabel ? `View game · ${seatsLabel}` : "View game"}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          flexShrink: 0,
          justifyContent: hasRowExtras ? { xs: "flex-start", sm: "flex-end" } : "flex-end"
        }}
        // Pay / Cancel / Check in act on the booking in place; on a class row
        // they must not also open the session page.
        onClick={(e) => e.stopPropagation()}
      >
        <Chip
          size="small"
          label={isPendingHold ? "RESERVED" : booking.bookingStatus}
          color={BOOKING_PILL_COLOR[booking.bookingStatus] || "default"}
          variant="outlined"
          sx={{ height: "1.375rem", fontSize: "0.7rem", fontWeight: 500, letterSpacing: 0.3 }}
        />
        {booking.rejoined && (
          <Chip
            size="small"
            label="RE-JOINED"
            color="info"
            variant="outlined"
            sx={{ height: "1.375rem", fontSize: "0.7rem", fontWeight: 500, letterSpacing: 0.3 }}
          />
        )}
        {isPendingHold && (
          <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "warning.main" }}>
            <LockOutlinedIcon sx={{ fontSize: "1rem" }} />
            <Typography variant="caption" fontWeight={600}>
              Held for your package purchase
            </Typography>
          </Box>
        )}
        {isRefunded ? (
          <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "info.main" }}>
            <ReplayIcon sx={{ fontSize: "1rem" }} />
            <Typography variant="caption" fontWeight={600}>
              Refunded
            </Typography>
          </Box>
        ) : isRefundPending ? (
          <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "warning.main" }}>
            <ReplayIcon sx={{ fontSize: "1rem" }} />
            <Typography variant="caption" fontWeight={600}>
              Refund pending
            </Typography>
          </Box>
        ) : isWrittenOff ? (
          <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "text.secondary" }}>
            <MoneyOffIcon sx={{ fontSize: "1rem" }} />
            <Typography variant="caption" fontWeight={600}>
              Not charged
            </Typography>
          </Box>
        ) : (
          isPaid && (
            <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "success.main" }}>
              <CheckCircleIcon sx={{ fontSize: "1rem" }} />
              <Typography variant="caption" fontWeight={600}>
                Paid
              </Typography>
            </Box>
          )
        )}
        {showHoldChip && (
          <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "text.secondary" }}>
            <LockOutlinedIcon sx={{ fontSize: "1rem" }} />
            <Typography variant="caption" fontWeight={600}>
              Card held
            </Typography>
          </Box>
        )}
        {checkedIn && (
          <Box display="flex" alignItems="center" gap={0.5} sx={{ color: "success.main" }}>
            <HowToRegIcon sx={{ fontSize: "1rem" }} />
            <Typography variant="caption" fontWeight={600}>
              Checked in{checkedIn.label ? ` ${checkedIn.label}` : ""}
            </Typography>
          </Box>
        )}
        <CheckInHint phase={checkInPhase} opensAt={booking.checkInWindow?.opensAt} />
        {/* Until the host authorises the hold the court isn't secured and is
            released on a timer — and the club can't do it for them. That makes
            it the most urgent action on the row, so it gets a real button
            rather than the caption link that sits under the booking title. */}
        {needsHold && (
          <Button
            size="small"
            variant="contained"
            color="warning"
            onClick={() => onViewGame?.(booking)}
            sx={{ ...ACTION_ORDER, height: "1.75rem", px: 1.5, textTransform: "none", color: "common.white", boxShadow: "none" }}
          >
            Finish card hold
          </Button>
        )}
        {canCheckIn && (
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => onCheckIn(booking)}
            sx={{ ...ACTION_ORDER, height: "1.75rem", px: 1.5, textTransform: "none", color: "common.white", boxShadow: "none" }}
          >
            Check in
          </Button>
        )}
        {showCalendar && <AddToCalendarMenu variant="icon" calendar={booking.calendar} sx={ACTION_ORDER} />}
        {canPay && (
          <Button
            size="small"
            variant="contained"
            disabled={payLoadingId === booking._id}
            onClick={() =>
              // A booking with an open UNPAID invoice is paid online through the
              // existing pay-link; one with no invoice yet opens the settle dialog,
              // where the client picks a package/voucher or pays online.
              booking.invoiceId ? onPayNow(booking.invoiceId, booking._id, booking._id) : onSettleBooking(booking)
            }
            sx={{ ...ACTION_ORDER, height: "1.75rem", px: 1.5, textTransform: "none", color: "common.white", boxShadow: "none" }}
          >
            {payLoadingId === booking._id ? <CircularProgress size={16} /> : "Pay Now"}
          </Button>
        )}
        {canReschedule && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<EventRepeatIcon sx={{ fontSize: "0.875rem" }} />}
            onClick={() => onReschedule(booking)}
            sx={PILL_BUTTON_SX}
          >
            Reschedule
          </Button>
        )}
        {showPackHint && (
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            icon={<InfoOutlinedIcon sx={{ fontSize: "0.875rem" }} />}
            label={packHint}
            title={booking.rescheduleNote || undefined}
            sx={{ ...ACTION_ORDER, height: "1.75rem", fontSize: "0.75rem", fontWeight: 500 }}
          />
        )}
        {/* Pill-shaped and sized to match the "Contact club to cancel" marker
            that replaces it on services the club cancels itself — the two read
            as the same slot, one actionable and one not. */}
        {canCancel && (
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<CancelOutlinedIcon sx={{ fontSize: "0.875rem" }} />}
            onClick={() => onCancel(booking)}
            sx={PILL_BUTTON_SX}
          >
            Cancel
          </Button>
        )}
        {/* Takes the Cancel button's slot, at button height and in amber: the
            action line keeps the same pill rhythm as every other row, and a
            client who came to drop the session sees why there's no button
            rather than scanning past a grey sentence. */}
        {showClubOnlyCancelHint && (
          <Chip
            size="small"
            variant="outlined"
            color="warning"
            icon={<InfoOutlinedIcon sx={{ fontSize: "0.875rem" }} />}
            label="Contact club to cancel"
            sx={{ ...ACTION_ORDER, height: "1.75rem", fontSize: "0.75rem", fontWeight: 500 }}
          />
        )}
      </Box>
    </RowShell>
  );
}

export default BookingRow;
